import Link from "next/link";
import { ProductDetailsResponse } from "@/types/product";
import { priceSymbol } from "@/utils/price-count";
import { Typography } from "@/components/typography";
import StarIcon from "@/assets/icons/star";
import ProductCardImage from "./product-card-image";
import { ProductNameWithTooltip } from "./ProductNameWithTooltip";

const DIV_CLASSNAMES = {
  featureProducts:
    "md:w-full w-full lg:max-w-[23%] md:p-5 lg:p-0 mb-3 mx-auto relative cursor-pointer",
  relatedProduct: "md:w-[32%] lg:w-[24%] xl:w-[19%] aspect-auto",
  products: "w-full aspect-auto",
};

interface Props {
  product: ProductDetailsResponse;
  pageType: "featureProducts" | "relatedProduct" | "products";
  hideDescription?: boolean;
}

export const ProductCardUI = ({
  product,
  pageType,
  hideDescription,
}: Props) => {
  const {
    name,
    price,
    flatDiscount,
    description,
    slug,
    id,
    categoryId,
    rating,
    reviewCount,
  } = product;

  const discountPercent =
    price > 0 && flatDiscount > 0
      ? Math.round((flatDiscount / price) * 100)
      : 0;

  const showRating = rating != null && reviewCount != null;

  return (
    <div className={`h-full flex flex-col ${DIV_CLASSNAMES[pageType]}`}>
      <div key={id} className="w-full aspect-auto h-full flex flex-col min-h-0">
        <div className="bg-white rounded-lg shadow-md overflow-hidden p-4 h-full flex flex-col flex-1 min-h-0 border border-gray-200">
          <div className="relative w-full aspect-square overflow-hidden rounded-md border bg-white">
            <Link href={`/products/${slug}`} className="block w-full h-full">
              <ProductCardImage product={product} />
            </Link>
            {flatDiscount > 0 && (
              <div className="absolute top-2 left-2 bg-yellow text-black text-xs font-semibold px-2 py-1 rounded-sm">
                Flat {priceSymbol(flatDiscount)} off
              </div>
            )}
          </div>

          <div className="pt-3 flex-1 flex flex-col min-h-0">
            {categoryId?.name && (
              <Typography tag="p" className="text-gray-400 text-sm m-0 mb-1">
                {categoryId.name}
              </Typography>
            )}

            <Link href={`/products/${slug}`} className="block">
              <ProductNameWithTooltip name={name} className="my-1" />
              {!hideDescription && description && (
                <Typography
                  tag="p"
                  className="text-gray-600 text-sm m-0 mt-2 line-clamp-2"
                >
                  {description}
                </Typography>
              )}
            </Link>

            {showRating && (
              <div className="flex items-center gap-1 mt-2 text-gray-600 text-sm">
                <StarIcon />
                <span>
                  {rating} ({reviewCount})
                </span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Typography tag="span" className="text-primary font-bold text-lg">
                {priceSymbol(price - flatDiscount)}
              </Typography>
              {flatDiscount > 0 && (
                <>
                  <Typography
                    tag="span"
                    className="text-gray-400 line-through text-sm"
                  >
                    {priceSymbol(price)}
                  </Typography>
                  <Typography
                    tag="span"
                    className="text-primary text-sm font-medium"
                  >
                    {discountPercent}% off
                  </Typography>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
